/* eslint-disable no-console */
/**
 * Ported from plm/portal/ui/lib/quickTests/web/legacy/VisualizePath.js
 *
 * Differences from the original:
 *  - Converted CommonJS require/module.exports to ESM imports/default export.
 *  - Removed jQuery dependency. DOM access uses native APIs; iteration uses lodash.
 *      $('#id')                      -> document.getElementById('id')
 *      this.contElem.append(html)    -> this.contElem.insertAdjacentHTML('beforeend', html)
 *      .addClass / .removeClass      -> classList.add / classList.remove
 *      $.extend                      -> Object.assign
 *      $.each                        -> _.forEach (note argument order: (item, idx))
 *      this.contElem.on('click', ..) -> this.contElem.addEventListener('click', ..)
 *  - Otherwise the rendering logic and Kinetic.* usage is preserved as-is.
 */

import _ from 'lodash';
import Kinetic from 'kinetic';
import * as utils from './LegacyUtils';
import ColorSelector from './ColorSelector';

function VisualizePath(quickTest, traceRoute) {
    const me = this;
    this.quickTest = quickTest;
    this.pathPositions = {};
    this.VIEW_LEVEL_DETAILS = 'level_4';
    this.NODE_VERTICAL_SPACE_SUMMARY = 50;
    this.NODE_VERTICAL_SPACE_DETAILS = 120;
    this.probeInfoDivCSSModifier = '';
    this.rootInfoDivCSSModifier = '';
    this.color_selector = new ColorSelector();
    this.lockedId = null;
    this.lastMouseOverId = null;

    this.defaultOptions = {
        container: 'visualize-container',
        debug: false,
        lineColor: '#EFEFEF',
        lineShadowColor: '#CCCCCC',
        circleFillColor: '#CEDCE9',
        circleStrokeColor: '#CCCCCC',
        circleShadowColor: '#FAFAFA',
        circleStrokeWidth: 2,
        pathWidth: 275,
        pathHeight: 150,
        leftMargin: 30,
        topMargin: 30,
        nodeRadius: 15,
        nodeRadiusInternal: 75,
        nodeVerticalSpace: 120,
        noResponseColor: '#EFEFEF',
        privateVerticalSpace: 50,
        nodeHorizontalSpace: 25,
        nodeHorizontalSpaceInternal: 25,
        fontSize: 12,
        fontName: 'Roboto',
        fontColor: '#333333',
        rectangleFillColor: '#666666',
        rectangleStrokeColor: '#999999',
        rectangleStrokeWidth: 2,
        rootNodeHeight: 25,
        rootNodeWidth: 25,
        rootNodeVerticalSpace: 90,
        rootNodeHorizontalSpace: 50,
        destNodeHeight: 25,
        destNodeWidth: 25,
        vertical: true,
        internal: false,
        cloud_probe: false,
    };

    this.defaultDarkModeOptions = {
        lineColor: '#555',
        lineShadowColor: '#555',
    };

    this.init = function init(options) {
        if (options && options.debug) console.log('VisualizePath#init', options);

        this._validateOptions(options);

        this.options = {
            ...this.defaultOptions,
            ...options,
            ...(options && options.isDarkMode ? this.defaultDarkModeOptions : {}),
        };

        this.contElem = document.getElementById(this.options.container);
        if (this.contElem) {
            // prevent clicks on nodes from erasing active mouseovers
            this.contElem.addEventListener('click', (e) => {
                e.stopPropagation();
            });
        }

        if (this.options.vertical) {
            this.height =
                this.options.topMargin +
                this.options.rootNodeHeight +
                this.options.rootNodeVerticalSpace +
                (this.options.maxNodesInPath - 1) * (this.options.nodeRadius + this.options.nodeVerticalSpace) +
                this.options.destNodeHeight;

            this.width = this.options.pathWidth;
        } else {
            const horizontalSpace = this.options.internal
                ? this.options.nodeHorizontalSpaceInternal
                : this.options.nodeHorizontalSpace;
            const radius = this.options.internal ? this.options.nodeRadiusInternal * 2 : this.options.nodeRadius;

            this.width =
                this.options.internal && this.options.cloud_probe
                    ? 0
                    : this.options.leftMargin +
                      this.options.rootNodeWidth +
                      this.options.rootNodeHorizontalSpace +
                      this.options.maxNodesInPath * (radius + horizontalSpace) +
                      this.options.destNodeWidth;

            this.height = this.options.pathHeight;
        }

        this.stage = new Kinetic.Stage({
            container: this.options.container,
            x: 0,
            y: 0,
            width: this.width,
            height: this.height,
        });

        this.baseLayer = new Kinetic.Layer();
        this.nodesLayer = new Kinetic.Layer();

        this.nodesLayer.on('mouseover', this._onMouseOver);
        this.nodesLayer.on('mouseout', this._onMouseOut);
        this.nodesLayer.on('click', this._onMouseClick);

        this.stage.add(this.baseLayer);
        this.stage.add(this.nodesLayer);

        this.pathPositions.x = this.options.leftMargin;
        this.pathPositions.y = this.options.topMargin;

        return this;
    };

    this._validateOptions = function _validateOptions(options) {
        if (options && !options.maxNodesInPath) throw new Error('Parameter maxNodesInPath is required.');
        if (options && Number.isNaN(Number(options.maxNodesInPath)))
            throw new Error('Parameter totalPaths must be numeric.');
        if (options && !options.container)
            console.log('No container specified.  Default #visualize-container will be used.');
    };

    this._buildPaths = function _buildPaths() {
        if (this.options.debug) console.log('#_buildPaths - Building totalPaths:', this.options.totalPaths);

        if (this.options.vertical) {
            this._line(
                this.pathPositions.x,
                this.options.topMargin,
                this.pathPositions.x,
                this.height - this.options.destNodeHeight - 10,
                7,
                false
            );
        } else {
            let additionalSpace = 0;
            if (this.options.internal) additionalSpace = this.options.nodeRadiusInternal;

            this._line(
                this.options.rootNodeWidth + this.options.nodeHorizontalSpace + additionalSpace,
                this.pathPositions.y,
                this.width - this.options.destNodeWidth,
                this.pathPositions.y,
                7,
                false
            );

            const headLength = 25;
            const x2 = this.width - this.options.destNodeWidth;
            const x1 = x2 - headLength * Math.cos(Math.PI / 4);
            const y2 = this.pathPositions.y;
            const y1 = y2 + headLength * Math.sin(Math.PI / 4);
            const y3 = y2 - headLength * Math.sin(Math.PI / 4);

            this._line(x1, y1, x2, y2, 7, false);
            this._line(x1, y3, x2, y2, 7, false);
        }

        this.baseLayer.draw();
    };

    this._onMouseOver = function _onMouseOver(e) {
        const currentNodeId = e.target.getId();
        const currentElement = document.getElementById(currentNodeId);
        if (!currentElement) return;

        if (e.target.className === 'Rect') {
            currentElement.classList.add('root-info-div-hover');
        } else {
            currentElement.classList.add('node-info-div-hover');

            if (document.querySelectorAll('.active-mouseover').length === 0 && currentNodeId === me.lockedId) {
                adjustScreenLockText(me.lastMouseOverId, false);
                me.lastMouseOverId = null;
                me.lockedId = null;
            }

            if (currentNodeId === me.lockedId || currentNodeId === me.lastMouseOverId) {
                clearTimeout(me.timer);
                return;
            }

            me.lastMouseOverId = currentNodeId;

            if (me.lockedId !== null) {
                const lockedElement = document.getElementById(me.lockedId);
                if (lockedElement) lockedElement.classList.remove('active-mouseover');
                adjustScreenLockText(me.lockedId, false);
                me.lockedId = null;
            }

            currentElement.classList.add('active-mouseover');
        }
    };

    this._onMouseOut = function _onMouseOut(e) {
        const currentNode = e.target;
        const currentNodeId = currentNode.getId();
        const currentElement = document.getElementById(currentNodeId);
        if (!currentElement) return;

        if (currentNode.className === 'Rect') {
            currentElement.classList.remove('root-info-div-hover');
        } else {
            currentElement.classList.remove('node-info-div-hover');

            if (currentNodeId !== me.lockedId) {
                const timeout = () => {
                    currentElement.classList.remove('active-mouseover');
                    me.lastMouseOverId = null;
                };
                me.timer = setTimeout(timeout, 100);
            }
        }
    };

    this._onMouseClick = function _onMouseClick(e) {
        const currentNodeId = e.target.getId();
        if (currentNodeId === me.lockedId) {
            const lockedElement = document.getElementById(me.lockedId);
            if (lockedElement) lockedElement.classList.remove('active-mouseover');
            adjustScreenLockText(me.lockedId, false);
            me.lockedId = null;
        } else {
            me.lockedId = currentNodeId;
            const currentElement = document.getElementById(currentNodeId);
            if (currentElement) currentElement.classList.add('active-mouseover');
            adjustScreenLockText(currentNodeId, true);
        }
    };

    this.draw = function draw() {
        if (this.options.debug) console.log('#draw');
        this.nodesLayer.draw();
    };

    this.clear = function clear() {
        if (this.options.debug) console.log('#clear');

        this.nodesLayer.removeChildren();
        this.pathPositions = {};

        document.querySelectorAll('.node-info-div').forEach((el) => el.remove());
        document.querySelectorAll('.node-info-div-details').forEach((el) => el.remove());
        document.querySelectorAll('.root-info-div').forEach((el) => el.remove());
        document.querySelectorAll('.root-info-div-details').forEach((el) => el.remove());
    };

    this.addRootToPath = function addRootToPath(probe) {
        if (this.options.debug) console.log('#addRootToPath', probe);

        if (!this.options.vertical) {
            this.addRootToPathSummary(probe);
            return;
        }

        const x = this.pathPositions.x - this.options.rootNodeWidth / 2;
        const y = this.pathPositions.y - this.options.rootNodeHeight / 2;

        this._drawRootNode(probe.id, x, y, probe.number_of_hops);

        const posLeft = this.pathPositions.x + 18;
        const posTop = this.pathPositions.y - 18;

        const html = this.generateRootNodeHTML(
            probe,
            probe.id,
            `root-info-div${this.rootInfoDivCSSModifier}`,
            posLeft,
            posTop,
            true
        );
        this._appendHTML(html);

        this.pathPositions.y += this.options.rootNodeVerticalSpace;
    };

    this.addRootToPathSummary = function addRootToPathSummary(probe) {
        if (this.options.debug) console.log('#addRootToPathSummary', probe);

        let additionalSpace = 0;
        if (this.options.internal) additionalSpace = this.options.nodeRadiusInternal;
        this.pathPositions.x += this.options.rootNodeWidth + this.options.rootNodeHorizontalSpace + additionalSpace;
    };

    this.generateRootNodeHTML = function generateRootNodeHTML(probe, id, className, left, top, vertical) {
        let html = `<div id="${id}" class="${className}"`;
        html += ` style="top: ${top}px; left: ${left}px;">`;

        if (vertical) {
            const nodeLocation = this._addNodeLocation(probe, true);

            html += `<div class="truncate"><strong>From</strong> ${utils.xssCleaner(probe.name)}</div>`;
            if (!_.isEmpty(nodeLocation)) html += `<div class="truncate"><strong>In</strong> ${nodeLocation}</div>`;
            html += '<div class="root-info-div-extra level_1 level_4 level_3-hide level_2-hide">';
        } else {
            const nodeLocation = this._addNodeLocation(probe, false);

            html += `<div class="truncate"><strong style="font-size: 12pt;">${utils.xssCleaner(
                probe.name
            )}</strong></div>`;
            if (!_.isEmpty(nodeLocation)) html += `<div class="truncate" style="font-size: 9pt">${nodeLocation}</div>`;
            html += '<div class="horizontal-extra">';
        }

        if (probe.dnsName) html += `<div class="truncate">&nbsp;&nbsp${utils.xssCleaner(probe.dnsName)}</div>`;
        if (probe.network) html += `<strong>Internal</strong>&nbsp;&nbsp;${probe.network.ipv4Local}<br />`;

        html += '</div></div>';
        return html;
    };

    this.addNodeToPath = function addNodeToPath(agentId, node) {
        if (!this.options.vertical) {
            this.addNodeToPathSummary(agentId, node);
            return;
        }

        if (this.options.debug) console.log('#addNodeToPath', agentId, node);

        const hops = Number.isNaN(Number(node.hops)) ? 1 : node.hops;
        const radius = this.options.nodeRadius;

        let nodeX = this.pathPositions.x;
        let nodeY = this.pathPositions.y;

        this._addNodeDetails(node, this.pathPositions.x, this.pathPositions.y);

        if (node.ip == null) {
            this._drawNode(node.id, nodeX, nodeY, radius, hops, this.options.noResponseColor);
        } else if (node.private && !node.lastNode) {
            this._drawNode(node.id, nodeX, nodeY, radius, hops);
        } else if (node.destination_ip === node.ip || node.lastNode) {
            nodeX = this.options.leftMargin - this.options.destNodeWidth / 2;
            nodeY = this.pathPositions.y - this.options.destNodeHeight / 2;
            this._drawDestinationNode(node.id, nodeX, nodeY, this.options.destNodeHeight, this.options.destNodeWidth);
        } else {
            this._drawNode(node.id, nodeX, nodeY, radius, hops);
        }

        this.pathPositions.y += this.options.nodeRadius + this.options.nodeVerticalSpace;
    };

    this.addNodeToPathSummary = function addNodeToPathSummary(agentId, node) {
        if (this.options.internal) {
            this.addNodeToPathInternal(agentId, node);
            return;
        }

        if (this.options.debug) console.log('#addNodeToPath', agentId, node);

        const circleText = getShortNodeTitle(node);
        const radius = this.options.nodeRadius;
        const nodeX = this.pathPositions.x;
        const nodeY = this.pathPositions.y;

        if (!node.lastNode) this._drawNode(node.id, nodeX, nodeY, radius, circleText);

        this._addNodeDetailsSummary(node, this.pathPositions.x, this.pathPositions.y);
        this.pathPositions.x += this.options.nodeRadius + this.options.nodeHorizontalSpace;
    };

    this.addNodeToPathInternal = function addNodeToPathInternal(agentId, node) {
        if (this.options.debug) console.log('#addNodeToPath', agentId, node);

        let circleText = '?';
        if (node.dns) circleText = node.dns;
        else if (node.ip) circleText = node.ip;
        const radius = this.options.nodeRadius;
        let nodeX = this.pathPositions.x;
        const nodeY = this.pathPositions.y;

        if (!node.lastNode) {
            nodeX -= 40;
            this._drawInternalNode(node.id, nodeX, nodeY, radius, circleText);
        }

        this._addNodeDetailsInternal(node, this.pathPositions.x, this.pathPositions.y);
        this.pathPositions.x += this.options.nodeRadiusInternal * 2 + this.options.nodeHorizontalSpaceInternal;
    };

    this._addNodeDetails = function _addNodeDetails(node, x, y) {
        if (this.options.debug) console.log('#_addNodeDetails', node, x, y);

        const lastNodeClass = node.lastNode ? ' dest-info-div' : '';
        const posLeft = x + 17;
        const posTop = y - 13;

        let html = `<div id="${node.id}" class="node-info-div${this.probeInfoDivCSSModifier}${lastNodeClass} fade in"`;
        html += ` style="top: ${posTop}px; left: ${posLeft}px;">`;

        if (node.private) {
            if (node.ip) {
                html += node.ip;
                if (node.dns && node.dns !== node.ip) html += `<div class="truncate">${node.dns}</div>`;
            } else if (node.lastNode) {
                html += '<div>The remaining hops did not respond</div>';
            } else if (node.hops && node.hops > 1) {
                html += `<div>These ${node.hops} hops did not respond</div>`;
            } else {
                html += '<div>This hop did not respond</div>';
            }
        } else {
            const location = this._addNodeLocation(node, true);
            if (node.lastNode) {
                html += `<strong>${utils.xssCleaner(me.quickTest.name)}</strong><br />`;
                html += location;
            } else {
                const nodeTitle = getNodeTitle(node);
                html += `<div class="node-info truncate"><span style="font-weight: bold;">${nodeTitle}</span></div>`;
                html += `<div class="node-info level_1-hide truncate">${location}</div>`;

                if (node.isp) {
                    const lcIsp = node.isp.toLowerCase();
                    const lcTitle = nodeTitle.toLowerCase();
                    if (lcTitle !== lcIsp && lcTitle.indexOf(lcIsp) === -1 && lcIsp.indexOf(lcTitle) === -1) {
                        html += `<div class="node-info level_1-hide truncate">ISP: ${node.isp}</div>`;
                    }
                }

                if (node.asn)
                    html += `<div class="node-info level_3-hide level_2-hide level_1-hide truncate">ASN: ${node.asn}</div>`;

                if (node['sub-nodes']) {
                    html += '<div class="node-info level_2-hide level_1-hide">';
                    html += '<dl class="dl-ips dl-horizontal">';
                    html += `<dt>IPs:</dt><dd>${node.ip}`;
                    if (node.dns && node.dns !== node.ip) html += ` (${node.dns})`;
                    html += '</dd>';

                    _.forEach(node['sub-nodes'], (subNode) => {
                        html += `<dt>&nbsp;</dt><dd>+ ${subNode.ip}<br />`;
                        if (subNode.dns && subNode.dns !== subNode.ip) html += subNode.dns;
                        html += '</dd>';
                    });
                    html += '</dl>';
                    html += '</div>';
                } else {
                    html += `<div class="node-info level_2-hide level_1-hide truncate">${node.ip}</div>`;
                    if (node.dns && node.dns !== node.ip)
                        html += `<div class="node-info level_2-hide level_1-hide truncate">${node.dns}</div>`;
                    html += '</div>';
                }
            }
        }

        html += '</div>';
        this._appendHTML(html);
    };

    this._addNodeDetailsSummary = function _addNodeDetailsSummary(node, x, y) {
        if (node.lastNode) {
            this._addDestinationDetailsSummary(node, x, y);
            return;
        }

        const nodeTitle = getNodeTitle(node);
        const posLeft = x - 50;
        const posTop = y + 27;

        let html = `<div id="${node.id}" class="node-info-div-summary fade in" style="top: ${posTop}px; left: ${posLeft}px;">`;
        html += `<div class="node-title"><span style="font-weight: bold; white-space: nowrap">${nodeTitle}&nbsp;&nbsp;</span>`;
        html +=
            '<span class="screen-lock" style="text-align: right; font-size: 10px; float: right; width: 80px;">&nbsp;Click to lock&nbsp;';
        html += '<i class="icon-unlock-alt"></i></span>';
        html += '</div>';
        html += `<div class="node-hop-count">${pluralizeHopCount(node.hops)} </div>`;

        if (node['sub-nodes']) {
            html += '<div class="node-ip-list">';
            html += node.ip;
            if (node.dns && node.dns !== node.ip) html += ` (${node.dns})`;
            html += '<br />';

            _.forEach(node['sub-nodes'], (subNode) => {
                html += subNode.ip ? subNode.ip : 'No response';
                if (subNode.ip && subNode.dns && subNode.dns !== subNode.ip) html += ` (${subNode.dns})`;
                html += '<br />';
            });

            html += '</div>';
        } else {
            html += '<div class="node-ip" style="white-space: nowrap">';
            html += node.ip;
            if (node.dns && node.dns !== node.ip) html += ` (${node.dns})`;
            html += '<br /></div>';
        }

        html += '</div>';
        this._appendHTML(html);
    };

    this._addNodeDetailsInternal = function _addNodeDetailsInternal(node, x, y) {
        if (node.lastNode) {
            this._addDestinationDetailsSummary(node, x - this.options.nodeRadiusInternal, y);
            return;
        }

        if (node.dns !== node.ip) {
            const posLeft = x - 50;
            const posTop = y + 27;

            let html = `<div id="${node.id}" class="node-info-div-summary fade in" style="top: ${posTop}px; left: ${posLeft}px;">`;
            html += `<div class="node-title"><span style="font-weight: bold; white-space: nowrap">${node.ip}</span></div>`;
            html += '</div>';
            this._appendHTML(html);
        }
    };

    function adjustScreenLockText(nodeID, isLocked) {
        if (!nodeID) return;
        const root = document.querySelector(`.node-info-div-summary#${cssIdEscape(nodeID)}`);
        if (!root) return;
        const el = root.querySelector('.screen-lock');
        if (!el) return;

        const lockedText = isLocked ? 'unlock' : 'lock';
        const iconClass = isLocked ? 'icon-lock' : 'icon-unlock-alt';
        let html = `&nbsp;&nbsp;Click to ${lockedText}&nbsp;`;
        html += `<i class="${iconClass}"></i>`;

        el.innerHTML = html;
    }

    function cssIdEscape(id) {
        // CSS.escape isn't safe on older browsers used by tests; do a minimal escape.
        return String(id).replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
    }

    function getNodeTitle(node) {
        let nodeTitle = '';
        if (node.as_org && node.as_org.indexOf('Block of') === -1) {
            nodeTitle = node.as_org;
        } else if (node.isp) {
            nodeTitle = node.isp;
        } else if (node.firstNode) {
            nodeTitle = 'Gateway Router';
        } else {
            nodeTitle = 'Unknown Organization';
        }
        return nodeTitle;
    }

    function getShortNodeTitle(node) {
        return traceRoute.name_selector.getName(getNodeTitle(node));
    }

    this._addDestinationDetailsSummary = function _addDestinationDetailsSummary(node, x, y) {
        const posLeft = x;
        const posTop = y - this.options.destNodeHeight / 4 + 10;

        const location = this._addNodeLocation(node, false);

        const lastNodeClass = 'dest-info-div-horizontal';
        let html = '';
        if (location === '') {
            if (!node.ip) {
                // Vertically center on the destination bar (height = destNodeHeight) to match
                // the alignment used by _drawFailedTraceRoute. translateY(-50%) is applied via
                // the .trace-route-error CSS rule.
                const topY = y + this.options.destNodeHeight / 2;
                html += `<div class="trace-route-error error-large" style="top: ${topY}px; left: ${
                    x + 8
                }px;">Trace route timed out</div>`;
            } else {
                html += `<div id="${node.id}" class="${lastNodeClass} fade in"`;
                html += ` style="top: ${posTop}px; left: ${posLeft}px;">`;
                html += '<strong style="font-size: 12pt;">';
                html += node.dns ? node.dns : '';
                html += '</strong><br />';

                if (node.ip && node.dns !== node.ip) html += `<div class="horizontal-extra">${node.ip}</div>`;
            }
        } else {
            html += `<div id="${node.id}" class="${lastNodeClass} fade in"`;
            html += ` style="top: ${posTop}px; left: ${posLeft}px;">`;
            html += '<strong style="font-size: 12pt;">';
            html += location;
            html += '</strong><br />';
            html += '<div class="horizontal-extra">';

            if (node.dns && node.dns !== node.ip) html += `${node.dns}<br />`;

            html += node.ip;
            html += '</div>';
        }

        html += '</div>';
        this._appendHTML(html);
    };

    this._drawFailedTraceRoute = function _drawFailedTraceRoute(vertical, x, y) {
        // Canvas is hidden in this branch, so anchor the message at the bar's start.
        const defaultX = vertical
            ? this.options.leftMargin
            : this.options.rootNodeWidth + this.options.nodeHorizontalSpace;
        x = x || defaultX;
        y = y || 0;

        this._hideCanvas();

        const errorSize = vertical ? 'small' : 'large';
        const style = `position:absolute; top:${y}px; left:${x}px; transform:none; color:darkgray;`;
        const html = `<div class="trace-route-error error-${errorSize}" style="${style}">Trace route is unavailable</div>`;

        this._appendHTML(html);
    };

    this._drawCloudProbeInternalSiteFailed = function _drawCloudProbeInternalSiteFailed(vertical, x, y) {
        // Canvas is hidden in this branch, so anchor the message at the bar's start.
        const defaultX = vertical
            ? this.options.leftMargin
            : this.options.rootNodeWidth + this.options.nodeHorizontalSpace;
        x = x || defaultX;
        y = y || 0;

        this._hideCanvas();

        const errorSize = vertical ? 'small' : 'large';
        const style = `position:absolute; top:${y}px; left:${x}px; transform:none; color:darkgray;`;
        const html = `<div class="trace-route-error error-${errorSize}" style="${style}">Cloud probe cannot test internal site</div>`;

        this._appendHTML(html);
    };

    /**
     * Hide all Kinetic canvases inside the container so that only the error
     * text is shown when trace route is unavailable.
     */
    this._hideCanvas = function _hideCanvas() {
        if (!this.contElem) return;
        // Ensure the absolutely-positioned error text is anchored to this container.
        if (getComputedStyle(this.contElem).position === 'static') {
            this.contElem.style.position = 'relative';
        }
        const canvases = this.contElem.querySelectorAll('canvas');
        _.forEach(canvases, (c) => {
            c.style.display = 'none';
        });
        // Kinetic wraps canvases in a div with class kineticjs-content; collapse its height
        const kc = this.contElem.querySelector('.kineticjs-content');
        if (kc) {
            kc.style.height = '0';
            kc.style.minHeight = '0';
            kc.style.padding = '0';
            kc.style.margin = '0';
        }
    };

    function pluralizeHopCount(count) {
        return count === 1 ? `${count} Hop` : `${count} Hops`;
    }

    this._addNodeLocation = function _addNodeLocation(node, hyperlink) {
        let html = '';

        if (node.city) {
            html += _.isUndefined(node.city.names) ? `${node.city}, ` : `${node.city.names.en}, `;
        }
        if (node.state && node.state !== 1) {
            html += _.isUndefined(node.state.names) ? `${node.state}, ` : `${node.state.names.en}, `;
        }

        if (node.country) {
            let country = _.isUndefined(node.country.names) ? node.country : node.country.names.en;
            if (country === 'United States') country = 'USA';
            if (html !== '') html += '&nbsp;&nbsp;';
            html += country;
        }

        if (html !== '' && hyperlink && node.latitude) {
            html += `&nbsp;<a class="map-link" target="_blank" title="Show on map" href="https://www.google.com/maps?q=${node.latitude},${node.longitude}&z=9"><i class="fa fa-map-marker"></i></a>&nbsp;`;
        }

        updateTestDetailsLocation(node, html);

        return html;
    };

    function updateTestDetailsLocation(node, html) {
        // Hack carried over from the portal/ui portal: if the legacy Test Details tab
        // has a span with id `spn-<agentId>-location`, populate it with the location HTML.
        // In react-portal that element doesn't exist; this becomes a no-op.
        const idPrefix = 'probe-';
        if (node && node.id && node.id.indexOf(idPrefix) > -1) {
            const agentId = node.id.replace(idPrefix, '');
            const span = document.getElementById(`spn-${agentId}-location`);
            if (span) span.innerHTML = `</br>${html}`;
        }
    }

    this._drawRootNode = function _drawRootNode(
        id,
        x,
        y,
        hopCount,
        height,
        width,
        fillColor,
        strokeColor,
        strokeWidth
    ) {
        if (this.options.debug) console.log('  #_drawRootNode', x, y);

        fillColor = this.shadeColor(fillColor || this.options.rectangleFillColor, 0.7);
        width = width || this.options.rootNodeWidth;
        height = height || this.options.rootNodeHeight;

        const rect = new Kinetic.Rect({
            id,
            x,
            y,
            fontFamily: this.options.fontName,
            width: width || this.options.rootNodeWidth,
            height: height || this.options.rootNodeHeight,
            fill: fillColor || this.options.rectangleFillColor,
            stroke: strokeColor || this.options.rectangleStrokeColor,
            strokeWidth: strokeWidth || this.options.rectangleStrokeWidth,
        });

        this.nodesLayer.add(rect);

        this._text(id, x + width / 2, y + height / 2, width, hopCount || 'X', 'center', 'bold');
    };

    this._drawDestinationNode = function _drawDestinationNode(
        id,
        x,
        y,
        height,
        width,
        fillColor,
        strokeColor,
        strokeWidth
    ) {
        if (this.options.debug) console.log('  #_drawDestinationNode', x, y);

        fillColor = this.shadeColor(fillColor || this.options.rectangleFillColor, 0.7);

        const rect = new Kinetic.Rect({
            id,
            x,
            y,
            fontFamily: this.options.fontName,
            width: width || this.options.rootNodeWidth,
            height: height || this.options.rootNodeHeight,
            fill: fillColor || this.options.rectangleFillColor,
            stroke: strokeColor || this.options.rectangleStrokeColor,
            strokeWidth: strokeWidth || this.options.rectangleStrokeWidth,
        });

        this.nodesLayer.add(rect);
    };

    this._line = function _line(
        fromX,
        fromY,
        toX,
        toY,
        strokeWidth,
        isDashed,
        fillColor,
        shadowBlur,
        xShadowOffest,
        yShadowOffest
    ) {
        if (this.options.debug) console.log('  #_line', fromX, fromY, toX, toY);

        const line = new Kinetic.Line({
            points: [fromX, fromY, toX, toY],
            shadowBlur: shadowBlur || 0,
            shadowOffsetX: xShadowOffest || 0,
            shadowOffsetY: yShadowOffest || 0,
            stroke: fillColor || me.options.lineColor,
            strokeWidth: strokeWidth || 1,
            dash: [20, 5],
            dashEnabled: isDashed || false,
        });

        this.baseLayer.add(line);
    };

    this._arrowLine = function _arrowLine(fromX, fromY, toX, toY, isDashed) {
        if (this.options.debug) console.log('  #_arrowLine', fromX, fromY, toX, toY);
        const headLen = 7;

        const angle = Math.atan2(toY - fromY, toX - fromX);
        const line = new Kinetic.Line({
            points: [
                fromX,
                fromY,
                toX,
                toY,
                toX - headLen * Math.cos(angle - Math.PI / 6),
                toY - headLen * Math.sin(angle - Math.PI / 6),
                toX,
                toY,
                toX - headLen * Math.cos(angle + Math.PI / 6),
                toY - headLen * Math.sin(angle + Math.PI / 6),
            ],
            stroke: '#666',
            shadowColor: me.options.lineShadowColor,
            strokeWidth: 2,
            shadowBlur: 3,
            shadowOffsetX: 0,
            shadowOffsetY: 2,
            dash: [20, 5],
            dashEnabled: isDashed,
        });

        this.nodesLayer.add(line);
    };

    this._drawNode = function _drawNode(
        id,
        x,
        y,
        radius,
        circleText,
        fillColor,
        strokeColor,
        strokeWidth,
        shadowColor
    ) {
        if (this.options.debug) console.log('  #_drawNode', id, x, y, radius, circleText);

        fillColor = fillColor || this.options.circleFillColor;
        strokeColor = fillColor;

        const circle = new Kinetic.Circle({
            id,
            x,
            y,
            radius,
            fill: fillColor,
            fontFamily: this.options.fontName,
            stroke: strokeColor,
            strokeWidth: strokeWidth || this.options.circleStrokeWidth,
            shadowColor: shadowColor || this.options.circleShadowColor,
            shadowOffsetX: 1,
            shadowOffsetY: 1,
        });

        this.nodesLayer.add(circle);

        if (circleText !== '') this._text(id, x, y, radius * 1.2, circleText, 'center', 'bold');
    };

    this._drawInternalNode = function _drawInternalNode(
        id,
        x,
        y,
        radius,
        circleText,
        fillColor,
        strokeColor,
        strokeWidth,
        shadowColor
    ) {
        if (this.options.debug) console.log('  #_drawNode', id, x, y, radius, circleText);

        fillColor = fillColor || this.options.circleFillColor;
        strokeColor = fillColor;

        const ellipse = new Kinetic.Ellipse({
            id,
            x,
            y,
            radius: { y: radius, x: this.options.nodeRadiusInternal },
            fill: fillColor,
            stroke: strokeColor,
            fontFamily: this.options.fontName,
            strokeWidth: strokeWidth || this.options.circleStrokeWidth,
            shadowColor: shadowColor || this.options.circleShadowColor,
            shadowOffsetX: 1,
            shadowOffsetY: 1,
        });

        this.nodesLayer.add(ellipse);

        if (circleText !== '') this._text(id, x, y, 2 * this.options.nodeRadiusInternal, circleText, 'center', 'bold');
    };

    this.shadeColor = function shadeColor(color, percent) {
        const f = parseInt(color.slice(1), 16);
        const t = percent < 0 ? 0 : 255;
        const p = percent < 0 ? percent * -1 : percent;
        const R = Math.floor(f / 0x10000);
        const G = Math.floor(f / 0x100) % 0x100;
        const B = f % 0x100;
        return `#${(
            0x1000000 +
            (Math.round((t - R) * p) + R) * 0x10000 +
            (Math.round((t - G) * p) + G) * 0x100 +
            (Math.round((t - B) * p) + B)
        )
            .toString(16)
            .slice(1)}`;
    };

    this._text = function _text(id, x, y, width, text, align, style, size) {
        if (this.options.debug) console.log('  #_text', id, x, y, width, text, align, style);

        const ktext = new Kinetic.Text({
            id,
            x,
            y,
            width,
            text,
            fontSize: size || this.options.fontSize,
            fontFamily: this.options.fontName,
            fontStyle: style,
            fill: this.options.fontColor,
            align,
        });

        ktext.setOffset({ x: ktext.getWidth() / 2, y: ktext.getHeight() / 2 });
        this.nodesLayer.add(ktext);
    };

    this.setLevelDetail = function setLevelDetail(detailLevel) {
        this.detailLevel = detailLevel;

        if (detailLevel === this.VIEW_LEVEL_DETAILS) {
            this.options.nodeVerticalSpace = this.NODE_VERTICAL_SPACE_DETAILS;
            this.probeInfoDivCSSModifier = '-details';
            this.rootInfoDivCSSModifier = '-details';
        } else {
            this.options.nodeVerticalSpace = this.NODE_VERTICAL_SPACE_SUMMARY;
            this.probeInfoDivCSSModifier = '';
            this.rootInfoDivCSSModifier = '';
        }
    };

    /**
     * Internal helper to append raw HTML into the visualization container.
     * Replaces jQuery's $.append() on this.contElem.
     */
    this._appendHTML = function _appendHTML(html) {
        if (this.contElem) this.contElem.insertAdjacentHTML('beforeend', html);
    };
}

export default VisualizePath;
